import type { GetScheduleFlexibleWorkingHoursView } from 'owa-graph-schema';
import { owaDate, compare } from 'owa-datetime';

export default function mergeFlexibleWorkingHours(
    existingHoursView: GetScheduleFlexibleWorkingHoursView[],
    newHoursView: GetScheduleFlexibleWorkingHoursView[]
): GetScheduleFlexibleWorkingHoursView[] {
    // Although we generally keep the existing working hours sorted, it's possible
    // for them to be out of order when initially stored, so to be safe we should still
    // do a sort before merging.
    existingHoursView.sort((a, b) =>
        compare(
            owaDate(a.start.timeZone.name, a.start.dateTime),
            owaDate(b.start.timeZone.name, b.start.dateTime)
        )
    );

    newHoursView.sort((a, b) =>
        compare(
            owaDate(a.start.timeZone.name, a.start.dateTime),
            owaDate(b.start.timeZone.name, b.start.dateTime)
        )
    );

    let i = 0;
    let j = 0;
    const mergedView: GetScheduleFlexibleWorkingHoursView[] = [];

    while (i < existingHoursView.length || j < newHoursView.length) {
        if (i < existingHoursView.length && j < newHoursView.length) {
            const aStartTime = owaDate(
                existingHoursView[i].start.timeZone.name,
                existingHoursView[i].start.dateTime
            );
            const bStartTime = owaDate(
                newHoursView[j].start.timeZone.name,
                newHoursView[j].start.dateTime
            );
            if (aStartTime < bStartTime) {
                mergedView.push(existingHoursView[i]);
                i++;
            } else if (bStartTime < aStartTime) {
                mergedView.push(newHoursView[j]);
                j++;
            } else if (areWorkingHoursEqual(existingHoursView[i], newHoursView[j])) {
                mergedView.push(existingHoursView[i]);
                i++;
                j++;
            } else {
                mergedView.push(existingHoursView[i], newHoursView[j]);
                i++;
                j++;
            }
        } else if (i < existingHoursView.length) {
            mergedView.push(existingHoursView[i]);
            i++;
        } else {
            mergedView.push(newHoursView[j]);
            j++;
        }
    }

    return mergedView;
}

function areWorkingHoursEqual(
    a: GetScheduleFlexibleWorkingHoursView,
    b: GetScheduleFlexibleWorkingHoursView
): boolean {
    return (
        a.start.dateTime === b.start.dateTime &&
        a.start.timeZone.name === b.start.timeZone.name &&
        a.end.dateTime === b.end.dateTime &&
        a.end.timeZone.name === b.end.timeZone.name &&
        a.workLocationType === b.workLocationType
    );
}
